import { useContext, useReducer } from 'react'
import { getActiveRole } from '@fs/zion-admin-mode'
import { useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { createJob, getTools, uploadMultiPartInput } from '../../../../../services/toolsService'
import { deepCopy, isNotNullOrUndefinedOrEmpty, isNullOrUndefinedOrEmpty } from '../../../../../util/Utils'
import useNotifications from '../../../../../hooks/useNotifications'
import { PermissionsContext } from '../../../../app/PermissionsContext'

export default () => {}

/* eslint-disable no-underscore-dangle */

function getNewInitialState() {
  return deepCopy({
    tools: {},
    selectedTool: null,
    disableInputFile: false,
    disableInputTextbox: false,
    inputs: {
      initialRequestsPerSecond: '',
      initialItemsPerRequest: '',
      initialParameters: {
        idCounter: 0,
        list: [{ id: 0 }],
      },
      inputText: '',
      inputFile: null,
    },
  })
}

export function useCreateJob(refreshJobs) {
  const { error } = useNotifications()
  const { isRole } = useContext(PermissionsContext)
  const { t } = useTranslation()

  function notifyError(message, err) {
    error(`${message}. ${err?.response?.status} ${err?.response?.statusText}`)
  }

  const reducer = (_, currentState) => {
    const nextState = deepCopy(currentState)
    // Files cannot be cloned with JSON.parse(JSON.stringify())
    const inputFile = currentState.inputs.inputFile
    if (isNotNullOrUndefinedOrEmpty(inputFile)) {
      nextState.inputs.inputFile = new File([inputFile], inputFile.name)
    }
    return nextState
  }
  const reducerOutput = useReducer(reducer, getNewInitialState())
  let createJobState = reducerOutput[0]
  const setCreateJobState = reducerOutput[1]

  const createJobActions = {
    dialogOverlay: useOverlay(),
    openDialog: () => {
      createJobActions.dialogOverlay.open()
    },
    setSelectedTool: (selectedTool) => {
      createJobState.selectedTool = selectedTool
      createJobState.inputs.initialRequestsPerSecond = selectedTool.maxRequestsPerSecond
      createJobState.inputs.initialItemsPerRequest = selectedTool.maxItemsPerRequest
      createJobActions.updateCreateJobState()
    },
    setTools: (tools) => {
      const groupedTools = {}
      tools.forEach((tool) => {
        if (!groupedTools[tool.groupName]) {
          groupedTools[tool.groupName] = []
        }
        groupedTools[tool.groupName].push(tool)
      })
      createJobState.tools = groupedTools
      createJobActions.updateCreateJobState()
    },
    fetchTools: () => {
      getTools()
        .then((data) => {
          if (isRole('Data Admin 4')) {
            data.tools = data.tools.filter(
              (tool) =>
                tool.groupName === 'Reservations' &&
                (tool.name === 'Remove Reservation' || tool.name === 'Assign to Church')
            )
          }
          if (isRole('Russian Data Admin')) {
            data.tools = data.tools.filter(
              (tool) =>
                tool.groupName === 'Reservations' &&
                (tool.name === 'Remove Reservation' || tool.name === 'Assign to Church')
            )
          } else {
            data.tools.sort((x, y) => {
              if (isNullOrUndefinedOrEmpty(x.groupName) || isNullOrUndefinedOrEmpty(y.groupName)) {
                return x.name.localeCompare(y.name)
              }
              const groupSort = x.groupName.localeCompare(y.groupName)
              if (groupSort === 0) {
                return x.name.localeCompare(y.name)
              }
              return groupSort
            })
          }
          createJobActions.setTools(data.tools)
        })
        .catch((err) => {
          error(t('tools.dats.toolsError', 'Error getting tools: {error}', { error: err.message }))
        })
    },
    setInitialRequestsPerSecond: (initialRequestsPerSecond) => {
      createJobState.inputs.initialRequestsPerSecond = initialRequestsPerSecond
      createJobActions.updateCreateJobState()
    },
    setInitialItemsPerRequest: (initialItemsPerRequest) => {
      createJobState.inputs.initialItemsPerRequest = initialItemsPerRequest
      createJobActions.updateCreateJobState()
    },
    addInitialParameter: (index) => {
      createJobState.inputs.initialParameters.idCounter += 1
      createJobState.inputs.initialParameters.list.splice(index + 1, 0, {
        id: createJobState.inputs.initialParameters.idCounter,
      })
      createJobActions.updateCreateJobState()
    },
    removeInitialParameter: (index) => {
      createJobState.inputs.initialParameters.list.splice(index, 1)
      createJobActions.updateCreateJobState()
    },
    setInitialParameterKey: (index, key) => {
      createJobState.inputs.initialParameters.list[index].key = key
      createJobActions.updateCreateJobState()
    },
    setInitialParameterValue: (index, value) => {
      createJobState.inputs.initialParameters.list[index].value = value
      createJobActions.updateCreateJobState()
    },
    setInputText: (inputText) => {
      if (!(inputText == null || inputText === '')) {
        createJobState.disableInputFile = true
      } else {
        createJobState.disableInputFile = false
      }
      createJobState.inputs.inputText = inputText
      createJobActions.updateCreateJobState()
    },
    setUploadProgress: (progress) => {
      createJobState.uploadProgress = progress
      createJobActions.updateCreateJobState()
    },
    setInputFile: (inputFile) => {
      if (inputFile == null) {
        createJobState.disableInputTextbox = false
      } else {
        createJobState.disableInputTextbox = true
      }
      createJobState.inputs.inputFile = inputFile
      createJobActions.updateCreateJobState()
    },
    uploadData: async () => {
      // if file is defined use the file not the text
      const inputFile = createJobState.inputs.inputFile
      const inputText = isNullOrUndefinedOrEmpty(inputFile) ? createJobState.inputs.inputText || null : null // null text tells DATS to wait for a file

      const assignment = await getActiveRole()

      createJob(
        assignment,
        createJobState.selectedTool._links.self.href,
        inputText,
        createJobState.inputs.initialItemsPerRequest,
        createJobState.inputs.initialRequestsPerSecond,
        createJobState.inputs.initialParameters.list
      )
        .then((response) => {
          // If inputText was null, try inputFIle
          if (
            inputFile &&
            inputFile.size &&
            inputFile.size > 0 &&
            response &&
            response._links &&
            response._links.uploadInputFile
          ) {
            uploadMultiPartInput(inputFile, response, createJobActions)
              .then(() => {
                createJobActions.clearInputs()
                createJobActions.dialogOverlay.close()
                refreshJobs()
              })
              .catch((err) => {
                createJobActions.clearInputs()
                createJobActions.dialogOverlay.close()
                notifyError(`Error uploading input file to S3`, err)
              })
          }
          createJobActions.clearInputs()
          createJobActions.dialogOverlay.close()
          refreshJobs()
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          notifyError(`Error creating job`, err)
        })

      return Promise.resolve()
    },
    clearInputs: () => {
      const tempTools = deepCopy(createJobState.tools)
      createJobState = getNewInitialState()
      createJobState.tools = tempTools
      createJobState.uploadProgress = null
      createJobActions.updateCreateJobState()
    },
    close: () => {
      createJobActions.dialogOverlay.close()
      createJobActions.clearInputs()
    },
    updateCreateJobState: () => {
      setCreateJobState(createJobState)
    },
  }

  return { createJobState, createJobActions }
}

/* eslint-enable no-underscore-dangle */
